import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, ViewChild, Injector } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { FoodDbService } from 'services/food-db.service';
import { BodyCompositionDataChangedEvent, UserDb } from 'services/user-db.service';
import { PubSubService } from 'services/pubsub.service';
import { AuthService } from 'services/auth.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { confirmDiscardAsync } from 'providers/unsaved-changes-guard/unsaved-changes-guard';
import { AlertController } from 'providers/alert-controller';
import { LoggingService } from 'services/logging.service';
import { PopoverController } from 'providers/popover-controller';
import {
  CalorieIntakeBaseline, CheckupTrainingCounts, Log_WeeklyCheckup, WeeklyCheckupCompletionAction, readPositiveCalories, readTrainingCounts,
} from '@cf-shared/data-model/log-weekly-checkup';
import { DateTime } from 'luxon';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { getEntryLocalTimeInDesiredTimeZone, LogRecordState, LogRecordType } from '@cf-shared/data-model/user-log';
import { getMeasureUnitConversion, bfErrorTexts, weekdayInitialsMondayFirst } from 'util/formatting';
import { SettingsService } from 'services/settings.service';
import { DebugService } from 'services/debug.service';
import { AdjustmentActionReason } from '@cf-shared/plan-update-internals';
import { SvPlanHandlerService } from 'services/server/sv-plan-handler.service';
import { isPlanCheckFailed } from 'services/server/plan-check-failure';
import { Log_Workout } from '@cf-shared/data-model/log-workout';
import { PlanMerge, WorkoutLoaderService } from 'services/workout-loader.service';
import { BFMethod, Gender } from '@cf-shared/enums';
import { TrendChartDataset } from 'components/cs-trend-chart/cs-trend-chart';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { CsInfoCardType } from 'components/cs-info-card/cs-info-card';
import { BFData, FitnessState, ImmutableState, usesCalorieCycling, isUsersTrainingGoalGrowth } from '@cf-shared/data-model/user-state';
import { calculateBF } from '@cf-shared/data-model/bodyfat';
import { INFOODS } from '@cf-shared/data-model/food';
import { LoadingController } from 'providers/loading-controller';
import { TelemetryService } from 'services/telemetry.service';
import { ReviewPromptService } from 'services/review-prompt.service';
import { deepClone, deepOverwriteCouchSafe } from '@cf-shared/deep-helpers';
import { planForCheckupSolve } from './weekly-checkup-solve-input';
import { DatabaseWrapper , DocWatcher } from 'services/database-wrapper';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { VldGroupDirective } from 'directives/vld-group/vld-group';
import { IProgressPage, ProgressPageParams } from 'pages/progress/progress-interface';
import { CheckForPlanAdjustmentsSolved } from '@cf-shared/plan-update-interface';
import {
  CheckupFinalChoice,
  MealCalories,
  ProgramUpdateOutcome,
  UndeterminedReason,
  appRecommendedCalories,
  canModifyOwnTarget,
  checkupRecordValues,
  finalCheckupChoices,
  getProgramUpdateOutcome,
  getUndeterminedReason,
  isProgressCheckUndetermined,
} from './weekly-checkup-outcome';
import { applyCustomCaloriesToPendingPlan, markTargetChangedIfSolveDidNot } from './weekly-checkup-coach-resolution';
import { CalorieChangeDirection, calorieChangeFromCalories, isBelowMinimumIntake } from 'components/cs-calorie-change-input/calorie-change';
import { CalorieChangeAudience, CsCalorieChangeInputComponent } from 'components/cs-calorie-change-input/cs-calorie-change-input';
import { numberText } from 'services/body-comp-overview-model';
import { ProgressStatusRule, buildCheckupProgressSnapshot } from 'services/progress-status';
import { describeError } from '@cf-shared/error-describe';
import { BodyCompOverviewDataService, BodyCompOverviewRequest, completedCheckupOverviewRequest } from 'services/body-comp-overview-data.service';
import { ExerciseConfigurationPageResult } from 'pages/exercise-configuration/exercise-configuration-interface';
import { ITrainingOverviewPage } from 'pages/training-overview/training-overview-interface';
import { TRAINING_OVERVIEW_SESSIONS_STEP, TRAINING_OVERVIEW_WINDOW_DAYS } from 'services/training-overview';
import { ExerciseSettingsContext } from 'services/training-overview-actions';
import { TrainingOverviewActionsService } from 'services/training-overview-actions.service';
import { TrainingOverviewDataService } from 'services/training-overview-data.service';
import { BodyCompOverviewModel, OverviewTrendKind } from 'services/body-comp-overview-model';
import { ConfigDbService } from 'services/config-db.service';
import { isCoachingDashboardOnAsync, watchCoachingDashboard } from 'services/coaching-dashboard-flag';
import { isRunningTrendChartsOnAsync, watchRunningTrendCharts } from 'services/running-trend-flag';
import { CsCoachCheckupReview } from './cs-coach-checkup-review';
import { CsInputMeasureComponent } from 'components/cs-input-measure/cs-input-measure';
import { waitForAngularUpdate } from 'services/browser-utils';
import { WeeklyCheckupPageParams } from './weekly-checkup-interface';
import { VldProgressDirective } from 'directives/vld-progress/vld-progress';
import { CoachNotifyService } from 'services/coach-notify.service';
import { CoachNotifyCheckupAwaitingParams, CoachNotifyCheckupParams } from '@cf-shared/coach-notify-interface';
import { NotificationService } from 'services/notification.service';
import { ToastController } from 'providers/toast-controller';
import {
  WeeklyCheckupStep,
  WeeklyCheckupStepConditions,
  checkupLoadsOverview,
  nextVisibleStep,
  previousVisibleStep,
  visibleStepCount,
  visibleStepNumber
} from './weekly-checkup-steps';
import {
  buildPreviousWeekdayLogs,
  buildWeighInTrendSeries,
  countWorkoutProgressAndSetbacks,
  findLastMeasurementUnits,
  hasSkinfoldMeasurement,
  hasWaistMeasurement,
  listDaysInRange,
  mapEntriesByDayAndType,
  pickLatestBodyfatLog,
  WeekdayLogData,
} from './weekly-checkup-chart-data';

export { WeeklyCheckupStep };

@Component({
  selector: 'page-weekly-checkup',
  templateUrl: 'weekly-checkup-page.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
}) export class WeeklyCheckupPage extends CsPage<WeeklyCheckupPageParams> implements OnDestroy {
  protected LL = LL;
  protected UndeterminedReason = UndeterminedReason;
  @ViewChild(VldProgressDirective) vldProgress: VldProgressDirective;
  @ViewChild('calorieBaselineCard', { static: false }) calorieBaselineCard: VldGroupDirective;
  @ViewChild('nutritionLoggingCard', { static: false }) nutritionLoggingCard: VldGroupDirective;
  @ViewChild('bodyfatEstimationCard', { static: false }) bodyfatEstimationCard: VldGroupDirective;
  @ViewChild('customCalorieInput', { static: false }) customCalorieInput: CsInputMeasureComponent;

  // Expose enum to template
  WeeklyCheckupStep = WeeklyCheckupStep;

  logWeeklyCheckup: Log_WeeklyCheckup;
  loading = false;
  isFinishing = false;
  isProcessingBaseline = false;

  // Track current step using enum directly - this is the source of truth
  currentStep: WeeklyCheckupStep = WeeklyCheckupStep.Intro;

  /**
   * The two conditions that hide steps, read fresh every time. Whether progress is
   * undetermined is decided by a lookup that can still be running when this page
   * first paints, so the answer must never be cached.
   */
  private get stepConditions(): WeeklyCheckupStepConditions {
    return {
      showBodyfatEstimation: this.showBodyfatEstimationSection,
      progressUndetermined: this.isProgressUndetermined()
    };
  }

  // Display step number (1-based, counts only the steps this person walks)
  get progressStep(): number {
    return visibleStepNumber(this.currentStep, this.stepConditions);
  }

  get totalProgressSteps(): number {
    return visibleStepCount(this.currentStep, this.stepConditions);
  }

  trendingChartsLoading = false;

  CalorieIntakeBaseline = CalorieIntakeBaseline;

  Math = Math;

  protected weightTrendDatasets: TrendChartDataset[] = [];
  // a measurement-unit symbol (kg/lb), not translatable text
  protected weightUnit: TranslatedString;
  protected trendDaysWeightIn: string[] = [];

  protected skinfoldTrendDatasets: TrendChartDataset[] = [];
  // a measurement-unit symbol (mm), not translatable text
  protected skinfoldUnit: TranslatedString = untranslatedString('mm');
  protected showSkinfoldChart: boolean = false;

  protected waistTrendDatasets: TrendChartDataset[] = [];
  // a measurement-unit symbol (cm/in), not translatable text
  protected waistUnit: TranslatedString;
  protected showTapeChart: boolean = false;

  protected weightInExplanationMessage: TranslatedString;
  protected weightInExplanationType: CsInfoCardType = 'info';

  protected workoutProgressionDatasets: TrendChartDataset[] = [];
  protected trendDaysWorkout: string[] = [];

  protected trainingProgressExplanationMessage: TranslatedString;
  protected trainingProgressExplanationType: CsInfoCardType = 'info';

  protected prevWeekLogDays: WeekdayLogData[] = [];
  /** the ISO dates left ticked when the food step was last answered; undefined while that step has not been answered (#5456) */
  private loggedDayDatesToStore: string[] | undefined;

  // the Body Composition overview (#5456), shown in place of the three trend charts while the
  // coaching dashboard switch is on. see design-docs/coaching-dashboard.md
  protected coachingDashboardOn = false;
  protected overviewModel?: BodyCompOverviewModel;
  protected overviewLoading = false;
  protected overviewFailed = false;
  /** counts overview loads, so an answer that arrives after a newer request is dropped */
  private overviewLoadCounter = 0;
  private unwatchCoachingDashboard?: () => void;
  /** the straight 30/70 tunnel on the three small trend charts (#6627); their median line never changes */
  protected runningTrendOn = false;
  private unwatchRunningTrend?: () => void;
  /**
   * The two Training Overview counts for the check-up being filled in (#5456, phase 3): how many
   * exercises' newest session is marked the same, and how many down. Undefined until the load
   * answers and after a failed load; the row then shows its button alone and nothing is stored.
   */
  private liveTrainingCounts?: CheckupTrainingCounts;
  /** counts those loads, so an answer that arrives after a newer request is dropped */
  private trainingCountsLoadCounter = 0;
  /** false until `ionViewCanEnter` has run once; it runs again each time a page pushed from here closes */
  private hasEnteredBefore = false;
  protected maxCalories: number = 0;

  protected avgLoggedCalories: number;
  protected prevWeekRecommendedCalories: number;
  protected customCalorieIntake: number;
  protected calorieIntakeBaseline: number;

  protected recommendedAvgCalories: number;
  /** true when the solve at the calorie-baseline step had its cut stopped by the minimum daily intake (#5415) */
  protected clampedToMinimumIntake = false;
  protected recommendedAvgMealCalories: number;
  protected recommendedCutMealCalories: number;
  protected recommendedBulkMealCalories: number;

  protected selectedCalorieIntakeBaseline: CalorieIntakeBaseline = undefined;

  protected newPlan: FitnessPlan = undefined;
  protected newFitnessState: FitnessState = undefined;

  // ---- a person without a coach setting their own target on the last step (#5456, phase 2).
  // see design-docs/coaching-dashboard.md, "The last step's choices, and a target of one's own"
  protected CalorieChangeAudience = CalorieChangeAudience;
  @ViewChild('ownTargetInput', { static: false }) private ownTargetInput?: CsCalorieChangeInputComponent;
  /**
   * The plan the calorie step's solve produced, untouched. A typed target is always applied by
   * scaling a copy of THIS plan, never an already scaled one. `newPlan` is this same object
   * until a target is applied, and the scaled copy afterwards.
   */
  private solverPlan: FitnessPlan | undefined;
  /** the lowest daily target the solve would set for this person; kept for the warning and stored with the check-up */
  protected minimumDailyIntake: number | undefined;
  /** true when the solved plan shows cut and bulk meals instead of one average meal */
  private showsCutAndBulkMeals = false;
  /** the target the person applied, in whole kcal; undefined while the app's own number stands */
  protected userModifiedCalories: number | undefined;
  /** the scaled plan's meal figures; the `recommended…` fields keep the solver's, which a Decline still stores */
  private modifiedMeals: MealCalories = {};
  /** the choice picked on the last step while the switch is on; undefined until the person picks one */
  protected finalChoice: CheckupFinalChoice | undefined;
  protected CheckupFinalChoice = CheckupFinalChoice;
  /** what the own-target fields hold; undefined while a field is empty */
  protected modifyCalories: number | undefined;

  protected plan: FitnessPlan = undefined;

  protected showBodyfatEstimationSection = true;
  protected bfData: BFData = {
    method: null,
    chest_mm: [],
    suprailiac_mm: [],
    umbilical_mm: [],
    thigh_mm: [],
    tricep_mm: [],
  }
  protected gender: Gender;
  protected born: string;
  protected height_cm: number;
  protected immutableState: ImmutableState;
  advancedMethodsCalorieCycling: boolean;

  protected isBodyCompositionTrendAcceptable: boolean = false;
  protected checkForPlanAdjustmentResult: CheckForPlanAdjustmentsSolved | undefined;

  /**
   * The plan check could not run, so there is no progress verdict for this check-up
   * (df-5ba092626ea2). Set only by `onCheckupStart`; the page guard refuses to open the page at
   * all when the check already fails there. While it is set the progress step explains why and
   * the Next button stays disabled, so nobody finishes a check-up on an answer nobody computed.
   */
  protected hasPlanCheckFailed = false;

  /** latched in ionViewCanEnter — see `showsCoachReview` for why it is not recomputed */
  private isCoachReviewScreen = false;
  /** true once `isCoachReviewScreen` was decided; later runs of `ionViewCanEnter` leave it alone */
  private isCoachReviewScreenDecided = false;

  /** the coach's screen, present only while `isCoachReviewScreen` is true */
  @ViewChild(CsCoachCheckupReview) private coachReview?: CsCoachCheckupReview;

  // cached coach/client-context flags — computed once per page open in ionViewCanEnter to avoid
  // repeated localStorage / CouchDB reads during every template change-detection tick
  private _isCoached = false;
  private _isCoachManaging = false;

  // bound into the coach's screen by the template, which is why it is not private
  protected docWatcher: DocWatcher;

  // #4625 — did this checkup reach its result screen, and did it finish in a
  // way worth asking for a rating after? Set while the page is open, read once
  // it has closed. See `considerReviewPromptAfterCheckup`.
  private reachedResultStep = false;
  private reviewPromptArmed = false;

  constructor(
    public navCtrl: NavController,
    public popoverCtrl: PopoverController,
    public userDb: UserDb,
    public foodDb: FoodDbService,
    public auth: AuthService,
    public logging: LoggingService,
    protected debug: DebugService,
    private settings: SettingsService,
    private cd: ChangeDetectorRef,
    private svPlanHandler: SvPlanHandlerService,
    private loadingCtrl: LoadingController,
    
    private injector: Injector,
    private workoutLoader: WorkoutLoaderService,
    private telemetry: TelemetryService,
    private reviewPrompt: ReviewPromptService,
    private pubSub: PubSubService,
    private coachNotify: CoachNotifyService,
    private toastCtrl: ToastController,
    private alertCtrl: AlertController,
    private notificationService: NotificationService,
    private configDb: ConfigDbService,
    private overviewData: BodyCompOverviewDataService,
    private trainingOverviewData: TrainingOverviewDataService,
    private trainingOverviewActions: TrainingOverviewActionsService,
  ) {
    super();
  }

  // coach / client context — cached getters read from _isCoached / _isCoachManaging
  // populated in ionViewCanEnter. keeps template change-detection cheap.
  protected isCoached(): boolean {
    return this._isCoached;
  }
  protected isCoachManaging(): boolean {
    return this._isCoachManaging;
  }
  protected isAwaitingCoach(): boolean {
    return this.logWeeklyCheckup?.completionAction === WeeklyCheckupCompletionAction.AwaitingCoach;
  }
  /**
   * True when this page is showing the coach their client's submitted check-up to answer,
   * rather than showing the wizard.
   *
   * Decided ONCE, when the check-up loads, and deliberately never worked out again —
   * recomputing it would tear the coach's screen down in the middle of its own work. Why,
   * and what latching it costs: `design-docs/coach-mode.md`, "Coach review UI".
   */
  protected showsCoachReview(): boolean {
    return this.isCoachReviewScreen;
  }

  /**
   * The coach has answered the check-up and everything is written, so close the page.
   *
   * Arming the leave guard and closing belong together, which is why the coach's screen
   * asks for this instead of closing itself: a close without the flag would put the
   * "throw your answers away?" question in front of a coach who has just finished. The
   * same pairing appears on every other way out of this page — see the list in
   * `ionViewCanLeave`.
   */
  protected onCoachReviewResolved() {
    this.leaveWithoutAsking.set();
    void this.navCtrl.popAsync();
  }

  // step-6 "submit-to-coach" branch: a coached client (not the coach managing
  // them) with a solver-recommended adjustment routes through coach review
  // instead of accepting/declining inline. used in three template sites:
  // the step-6 body, the submit-to-coach button gate, and the decline/accept
  // button gate's negation. keeping them on one getter keeps the trio in lockstep.
  protected isCoachSubmissionMode(): boolean {
    return this.isCoached() && !this.isCoachManaging() && this.isAdjustmentNeeded();
  }
  // client looking at their own submitted-but-pending checkup — read-only waiting card
  protected isAwaitingFromClient(): boolean {
    return this.isAwaitingCoach() && !this.isCoachManaging();
  }

  hasClientSmallScreen = hasClientSmallScreen;

  ngOnDestroy() {
    this.docWatcher?.destroy();
    this.unwatchCoachingDashboard?.();
    this.unwatchRunningTrend?.();
  }

  async ionViewCanEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cd.markForCheck();
      });
    }

    // the running trend switch only adds the tunnel to the small charts, so it is read with the other switch
    this.runningTrendOn = await isRunningTrendChartsOnAsync(this.configDb);
    if (!this.unwatchRunningTrend) {
      this.unwatchRunningTrend = watchRunningTrendCharts(this.configDb, isOn => {
        if (this.viewDestroyed) return;
        this.runningTrendOn = isOn;
        this.cd.markForCheck();
      });
    }

    // the coaching dashboard switch is read before the first render (#5456), and followed afterwards
    this.coachingDashboardOn = await isCoachingDashboardOnAsync(this.configDb);
    if (!this.unwatchCoachingDashboard) {
      this.unwatchCoachingDashboard = watchCoachingDashboard(this.configDb, isOn => {
        if (this.viewDestroyed || isOn === this.coachingDashboardOn) return;
        this.coachingDashboardOn = isOn;
        if (isOn) {
          void this.loadCheckupOverviewAsync();
          void this.loadTrainingCountsAsync();
        } else {
          // with the switch off the row is gone, and no count may be stored on the check-up
          this.trainingCountsLoadCounter++;
          this.liveTrainingCounts = undefined;
          // the last step's choices and the own-target fields are gone too, so a typed target must not
          // stay armed: the switch-off Accept would save a number nobody can see any more
          if (!this.isFinishing) {
            this.clearOwnTarget();
            this.finalChoice = undefined;
          }
        }
        this.cd.markForCheck();
      });
    }

    // snapshot coach/client context once per page open — userDb helpers read localStorage / CouchDB
    // and are stable for the lifetime of this page (attach/detach reloads the app)
    this._isCoached = !!this.userDb.loadCoachRelAsClient()?.coachUid;
    this._isCoachManaging = !!this.userDb.getCoachManagingClient();

    // this guard runs again each time a page pushed from here closes (the Training Overview,
    // Exercise Settings, Edit Workouts). what such a page changed is loaded again: the coach's
    // Training tab when it has been opened, and the counts of the wizard's Training Overview row
    const isReturn = this.hasEnteredBefore;
    this.hasEnteredBefore = true;
    if (isReturn) {
      void this.coachReview?.reloadTrainingAsync();
      void this.loadTrainingCountsAsync();
    }

    let id = this.navParams.data.id;
    if (id) {
      try {
        this.logWeeklyCheckup = await this.userDb.getOneLogEntryAsync<Log_WeeklyCheckup>(id, this.docWatcher);

      } catch (err) {
        return this.auth.failPageGuardAsync(`Unknown meal id: '${id}'`, { cause: err });
      }

      // decide here, once, which of the two screens this page open is showing — see
      // `showsCoachReview` for why this must not be worked out again later
      // only on the first run: once the coach has answered, the check-up is no longer "awaiting",
      // and a later run would swap the coach's screen for the wizard under their hands
      if (!this.isCoachReviewScreenDecided) {
        this.isCoachReviewScreen = this.isAwaitingCoach() && this.isCoachManaging();
        this.isCoachReviewScreenDecided = true;
      }

      if (this.isCompleted()) {
        // if this page open is the coach's review screen, skip loading the plan and building
        // the trend charts. that screen reads neither: it takes the pending plan from the
        // check-up document and loads the live plan itself when the coach presses Apply. the
        // plan load is awaited, so doing it anyway would only delay the coach's screen.
        if (!this.showsCoachReview()) {
          this.plan = await this.loadCompletedCheckupPlanAsync();
          void this.onCompletedCheckupOpened();
        }
      } else {
        this.plan = await this.userDb.loadPlanAsync(this.docWatcher);
        await this.checkForNewerCheckup();
        // determine progress early so the stepper shows the correct step count
        try {
          this.checkForPlanAdjustmentResult = await this.svPlanHandler.checkForPlanAdjustments();
        } catch (error) {
          // The check-up cannot be shown without this. Its result sizes the stepper, decides
          // which steps are skipped, picks what the progress step says, and is stored on the
          // saved check-up document at the end -- so opening anyway would walk the user through
          // a wizard built on an answer nobody computed, and then write that answer down. The
          // owner's decision on df-5ba092626ea2 was to stop the user here rather than let them
          // through. The finish step runs a different solver entry point that throws outright on
          // the same inputs, so carrying on would very likely fail at the last step anyway.
          if (!isPlanCheckFailed(error)) throw error;
          return this.auth.failPageGuardAsync(
            `The plan check failed, so the weekly check-up cannot be shown: '${id}'`,
            { userMessage: LL.weeklyCheckup.errors.progressCheckFailed(), cause: error },
          );
        }
      }
    }
  }

  // the plan a finished check-up was worked out against: the copy saved with it if there is one,
  // otherwise the live plan
  private async loadCompletedCheckupPlanAsync(): Promise<FitnessPlan> {
    if (this.logWeeklyCheckup.fitnessPlanBackupId) {
      return this.userDb.getFitnessPlanBackupEntryAsync(this.logWeeklyCheckup.fitnessPlanBackupId);
    }
    return this.userDb.loadPlanAsync(this.docWatcher);
  }

  isCompleted(): boolean {
    if (this.isFinishing) return false;
    return this.logWeeklyCheckup && this.logWeeklyCheckup.state === LogRecordState.Done;
  }

  /**
   * Runs before the page closes, whichever way the user leaves: the header back
   * arrow, the browser back button, Android's hardware back button or a swipe
   * back. Without it, a check-up part-way through lost every answer in it — the
   * caliper or tape measurements especially, which are several numbers read off
   * the body one at a time (#5058).
   *
   * There is no saving a half-finished check-up: it is only written at the end,
   * because finishing runs the solver and rebuilds the plan. So the question is
   * whether the user meant to drop what they have entered.
   *
   * The page serves two very different people, so it asks two different
   * questions. A client is part-way through the wizard and stands to lose the
   * answers they have entered. A coach is looking at a check-up that is already
   * written, and stands to lose only the calorie target they typed over the
   * recommendation — so they get their own wording, because "the answers you
   * have entered" describes nothing they did.
   */
  async ionViewCanLeave(): Promise<boolean> {
    // every path that closes the wizard itself arms this — finishing it, declining
    // the adjustments, submitting it to a coach, and a coach resolving it — so the
    // guard does not then ask whether to throw the answers away.
    //
    // keep that list complete. It is what a maintainer adding another way out will
    // audit against, and a path missing from it is a "throw your answers away?"
    // dialog in front of somebody who has just finished.
    if (this.leaveWithoutAsking.consume()) return true;
    if (this.showsCoachReview()) {
      // the coach's screen owns that number, so it is the one that knows whether there is
      // anything to lose. the dialog stays here, beside the client's, so the two questions
      // this page can ask are written in one place.
      if (!this.coachReview?.hasUnsavedCalorieTarget()) return true;
      return confirmDiscardAsync(this.alertCtrl, {
        title: LL.unsavedChanges.discard.coachTargetTitle(),
        message: LL.unsavedChanges.discard.coachTargetMessage(),
      });
    }
    if (!this.hasUnsavedCheckupAnswers()) return true;
    return confirmDiscardAsync(this.alertCtrl, {
      title: LL.unsavedChanges.discard.checkupTitle(),
      message: LL.unsavedChanges.discard.checkupMessage(),
    });
  }

  /**
   * Whether the user has entered anything worth asking about.
   *
   * Deliberately NOT "are we past the first step". Walking forward through the
   * intro and the progress review enters nothing — those steps only show the
   * user their own history — so asking there would put a dialog in front of
   * somebody who has typed nothing at all.
   *
   * A finished or coach-pending check-up is excluded because the wizard's
   * answers are written by then and there is nothing left to lose. That is not
   * the same as "nothing on this page is unsaved" — a coach reviewing such a
   * check-up can still have a typed calorie target on screen, which the coach's own
   * screen answers for and the guard asks about separately.
   */
  private hasUnsavedCheckupAnswers(): boolean {
    if (this.isCompleted() || this.isPendingCheckup()) return false;
    const hasBodyfatEntry = !!this.bfData?.method;
    const hasBaselineChoice = this.selectedCalorieIntakeBaseline !== undefined;
    // The nutrition-logging step asks the user to untick the days they did not
    // log properly, and every day starts ticked — so an unticked day IS an
    // answer, and one that matters: those ticks decide the average the calorie
    // baseline is worked out from. It is also the answer most likely to be alone
    // on screen, because the body-fat step is hidden whenever the last reading is
    // under 30 days old, which is the ordinary case.
    const hasDeselectedLogDay = this.prevWeekLogDays.some(day => !day.checked);
    return hasBodyfatEntry || hasBaselineChoice || hasDeselectedLogDay;
  }

  // Step navigation helpers
  getPageTitle(): TranslatedString {
    switch (this.currentStep) {
      case WeeklyCheckupStep.Intro:
        return LL.weeklyCheckup.steps.intro();
      case WeeklyCheckupStep.ProgressReview:
        return LL.weeklyCheckup.steps.progressReview();
      case WeeklyCheckupStep.BodyfatEstimation:
        return LL.weeklyCheckup.steps.bodyfatEstimation();
      case WeeklyCheckupStep.NutritionLogging:
        return LL.weeklyCheckup.steps.nutritionLogging();
      case WeeklyCheckupStep.CalorieBaseline:
        return LL.weeklyCheckup.steps.calorieBaseline();
      case WeeklyCheckupStep.ProgramUpdate:
        return LL.weeklyCheckup.steps.programUpdate();
      default:
        return LL.weeklyCheckup.steps.intro();
    }
  }

  isLastStep(): boolean {
    return this.currentStep === WeeklyCheckupStep.ProgramUpdate;
  }

  isBodyfatStep(): boolean {
    return this.currentStep === WeeklyCheckupStep.BodyfatEstimation;
  }

  getStepLabel(): TranslatedString {
    return LL.weeklyCheckup.step({ current: this.progressStep, total: this.totalProgressSteps });
  }

  getDeclineOrCancelLabel(): TranslatedString {
    return this.isAdjustmentNeeded() ? LL.weeklyCheckup.buttons.decline() : LL.common.buttons.cancel();
  }

  // the right button acts on the EFFECTIVE outcome: with a target of their own applied there is a plan to accept
  getAcceptOrFinishLabel(): TranslatedString {
    return this.isPlanSaveNeeded() ? LL.weeklyCheckup.buttons.accept() : LL.common.buttons.finish();
  }

  async handleSkipBodyfatButton(): Promise<void> {
    // Clear bodyfat data first (while still on bodyfat step)
    this.bodyfatSkipped();
    // Wait for Angular to process the change and any validation
    await waitForAngularUpdate(this.injector);
    if (this.viewDestroyed) return;
    // Then progress to next step. Ask advanceToNextStep where to go rather than
    // naming a step here, so Skip hides the same steps every other navigation
    // path hides — when progress is undetermined that means jumping to the
    // final program-update step instead of showing the nutrition-logging step.
    this.advanceToNextStep();
  }

  handleBackButton(): void {
    if (this.isPendingCheckup()) return;
    const prevStep = previousVisibleStep(this.currentStep, this.stepConditions);
    // if there is a step behind this one, go back to it
    if (prevStep !== undefined) {
      this.currentStep = prevStep;
      this.cd.markForCheck();
    }
  }

  handleNextButton(): void {
    if (this.isPendingCheckup()) return;
    // Handle special actions before progressing based on current step
    let shouldProgress = true;
    switch (this.currentStep) {
      case WeeklyCheckupStep.Intro:
        void this.onCheckupStart();
        break;
      case WeeklyCheckupStep.ProgressReview:
        this.onProgressReviewNextButtonClick();
        break;
      case WeeklyCheckupStep.BodyfatEstimation:
        this.onBodyfatSectionNextButtonClick();
        break;
      case WeeklyCheckupStep.NutritionLogging:
        this.onNutritionLoggingNextButtonClick();
        break;
      case WeeklyCheckupStep.CalorieBaseline:
        void this.handleCalorieBaselineNext();
        return; // async handler will advance step
    }

    if (shouldProgress) {
      this.advanceToNextStep();
    }
  }

  private async handleCalorieBaselineNext(): Promise<void> {
    if (this.isProcessingBaseline) return;
    this.isProcessingBaseline = true;
    try {
      const shouldProgress = await this.onCalorieIntakeBaselineNextButtonClick();
      if (shouldProgress) {
        this.advanceToNextStep();
      }
    } finally {
      this.isProcessingBaseline = false;
    }
  }

  private advanceToNextStep(): void {
    const nextStep = nextVisibleStep(this.currentStep, this.stepConditions);
    // if there is a step ahead of this one, go on to it
    if (nextStep !== undefined) {
      this.currentStep = nextStep;
      // #4625 — remember that the person actually saw the result. The prompt
      // fires long after this page is gone, so it cannot ask which step is on
      // screen; this is the only moment that fact exists.
      if (nextStep === WeeklyCheckupStep.ProgramUpdate) this.reachedResultStep = true;
      this.cd.markForCheck();
    }
  }

  /**
   * #4625 — a weekly checkup finished. Ask for a rating once this page is gone.
   *
   * **Only a checkup the person actually completed counts.** `Accepted` means
   * they took the app's advice; `Finished` means no adjustment was needed and
   * they saw that. The two that must NOT fire are easy to get wrong:
   *
   * - `Declined` — they just turned the advice down. Note that the checkup's
   *   "an adjustment was needed" flag is true on this path too, and the decline
   *   path stores its answer and pops the page exactly like the accept path, so
   *   nothing except the completion action distinguishes them.
   * - `AwaitingCoach` — the checkup went to a coach and nothing has been
   *   produced for the person yet. There is no win to ask about.
   *
   * The scheduling is handed to `ReviewPromptService` deliberately. The prompt
   * must appear AFTER this page closes, and a timer owned by this page would be
   * cancelled by construction when the view is destroyed — see that service.
   */
  private considerReviewPromptAfterCheckup(completionAction: WeeklyCheckupCompletionAction): void {
    if (!this.reachedResultStep) return;
    if (completionAction !== WeeklyCheckupCompletionAction.Accepted
      && completionAction !== WeeklyCheckupCompletionAction.Finished) {
      return;
    }
    // ARMED here, fired after the page has actually closed. The delay must be
    // measured from the close, not from this point: on the accept path a cache
    // clear, a pub/sub publish and a loader dismiss still run after this, and
    // on a slow device those can outlast any fixed delay — which would put the
    // store's prompt on top of the checkup page or its spinner.
    this.reviewPromptArmed = true;
  }

  /**
   * #4625 — fire the armed checkup prompt, once this page is gone.
   *
   * Called immediately after the page pops. The scheduling itself belongs to
   * `ReviewPromptService` because that service outlives this page; a timer
   * owned here would be cancelled the moment the view is destroyed, which is
   * exactly the event being waited for.
   */
  private fireArmedReviewPromptIfAny(): void {
    if (!this.reviewPromptArmed) return;
    this.reviewPromptArmed = false;
    this.reviewPrompt.scheduleAfterCheckupPrompt();
  }

  isNextButtonDisabled(): boolean {
    // for progress review step, disable until chart data is loaded (needed for step skipping)
    if (this.currentStep === WeeklyCheckupStep.ProgressReview && this.trendingChartsLoading) {
      return true;
    }

    // the plan check failed after the page opened, so there is no verdict to carry the rest of
    // this check-up. Stop here rather than let it finish on a half-computed answer
    // (df-5ba092626ea2); the progress step is showing why.
    if (this.currentStep === WeeklyCheckupStep.ProgressReview && this.hasPlanCheckFailed) {
      return true;
    }

    // For bodyfat step, disable until a method is selected
    if (this.isBodyfatStep() && this.showBodyfatEstimationSection) {
      return !this.bfData?.method;
    }

    // For calorie baseline step, disable until a valid baseline is selected
    if (this.currentStep === WeeklyCheckupStep.CalorieBaseline) {
      if (!this.selectedCalorieIntakeBaseline) {
        return true;
      }
      // If custom is selected, require a valid positive number
      if (this.selectedCalorieIntakeBaseline === CalorieIntakeBaseline.Custom) {
        return !this.customCalorieIntake || this.customCalorieIntake <= 0;
      }
    }

    return false;
  }

  isFutureCheckup(): boolean {
    if (!this.logWeeklyCheckup) {
      return false;
    }
    const now = this.debug.getNowLocal();
    const checkupDate = getEntryLocalTimeInDesiredTimeZone(this.logWeeklyCheckup, now);
    return checkupDate.toISODate() > now.toISODate();
  }

  isPendingCheckup(): boolean {
    return this.isFutureCheckup() || this.hasNewerCheckup;
  }

  hasNewerCheckup = false;

  async checkForNewerCheckup(): Promise<void> {
    if (!this.logWeeklyCheckup || this.isCompleted()) {
      this.hasNewerCheckup = false;
      return;
    }

    const now = this.debug.getNowLocal();
    const currentCheckupDate = getEntryLocalTimeInDesiredTimeZone(this.logWeeklyCheckup, now);

    // Search for any checkups after this one (scheduled or completed)
    const searchStart = currentCheckupDate.plus({ days: 1 }).startOf('day');
    const searchEnd = now.endOf('day');

    const logEntries = await this.userDb.getLogEntriesInRangeAsync(searchStart, searchEnd, this.docWatcher, { inLogEntriesTimeZone: true });
    const weeklyCheckups = logEntries.filter(e => e.type === LogRecordType.WeeklyCheckup);

    this.hasNewerCheckup = weeklyCheckups.length > 0;
    this.safeMarkForCheck(this.cd);
  }

  async ngOnInit() {
    this.immutableState = await this.userDb.loadImmutableStateAsync(this.docWatcher);
    const fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);
    this.showBodyfatEstimationSection = false;
    if (!fitnessState.lastBfDataUpdate_utc || DateTime.fromISO(fitnessState.lastBfDataUpdate_utc) < this.debug.getNowLocal().toUTC().minus({ days: 30 })) {
      this.showBodyfatEstimationSection = true;
      this.gender = this.immutableState.gender;
      this.safeMarkForCheck(this.cd);
    }
  }

  async ionViewDidEnter() {
    // Determine if user's goal is weight gain (bulking) or not (cutting/recomp)
    const declaredState = await this.userDb.loadDeclaredStateAsync(this.docWatcher);
    const fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);
    if (!this.logWeeklyCheckup.isWeightGainGoal) {
      this.logWeeklyCheckup.isWeightGainGoal = isUsersTrainingGoalGrowth(
        declaredState.trainingGoalGrowth,
        this.immutableState.gender,
        fitnessState.bfData?.bf_pc
      );
    }
  }

  async updateWeighInTrendChartsData() {
    // Clear previous data to avoid stacking when navigating back and forth
    this.weightTrendDatasets = [];
    this.skinfoldTrendDatasets = [];
    this.waistTrendDatasets = [];

    let now = this.debug.getNowLocal();
    const dayDate = getEntryLocalTimeInDesiredTimeZone(this.logWeeklyCheckup, now);
    const untilDate = dayDate.endOf('day');
    const dataCollectionStartDate = untilDate.minus({ days: 13 }).startOf('day'); // Collect 14 days of data

    const logEntries = await this.userDb.getLogEntriesInRangeAsync(dataCollectionStartDate, untilDate, this.docWatcher, { inLogEntriesTimeZone: true });
    const immutableState = await this.userDb.loadImmutableStateAsync(this.docWatcher);

    // Create a sorted list of all 14 days for trend calculation
    const allDays = listDaysInRange(dataCollectionStartDate, untilDate);

    // the unit the user last measured in decides the conversion, so it is asked
    // for before the series are built
    const lastUnits = findLastMeasurementUnits(logEntries, allDays, now);
    let { factor: weightUnitFactor, unitName: weightUnitName } = getMeasureUnitConversion('addedweight_kg', this.settings.locale, lastUnits.weightUnit);
    let { factor: tapeUnitFactor, unitName: tapeUnitName } = getMeasureUnitConversion('bodydimensions_cm', this.settings.locale, lastUnits.tapeUnit);

    const series = buildWeighInTrendSeries({
      logEntries,
      days: allDays,
      gender: immutableState.gender,
      weightUnitFactor,
      tapeUnitFactor,
      now,
    });

    // Pass exact measurements to chart component
    // Get chart colors from CSS variables
    const rootStyles = getComputedStyle(document.documentElement);
    const chartPurple = rootStyles.getPropertyValue('--chart-purple').trim();
    const chartBlue = rootStyles.getPropertyValue('--chart-blue').trim();
    const chartGreen = rootStyles.getPropertyValue('--chart-green').trim();

    this.weightTrendDatasets.push({ name: 'Weight', values: series.weights, color: chartPurple });
    this.trendDaysWeightIn = series.dates;
    this.weightUnit = untranslatedString(weightUnitName);

    this.skinfoldTrendDatasets.push({ name: 'Skinfold', values: series.calipers, color: chartBlue });
    this.skinfoldUnit = untranslatedString('mm');

    this.waistTrendDatasets.push({ name: 'Waist', values: series.waists, color: chartGreen });
    this.waistUnit = untranslatedString(tapeUnitName);
  }

  async updateWorkoutTrendChartData() {
    // Clear previous data to avoid stacking when navigating back and forth
    this.workoutProgressionDatasets = [];

    let now = this.debug.getNowLocal();
    const dayDate = getEntryLocalTimeInDesiredTimeZone(this.logWeeklyCheckup, now);
    const untilDate = dayDate.minus({ days: 1 }).endOf('day');
    const displayStartDate = untilDate.minus({ days: 6 }).startOf('day');

    const logEntries = await this.userDb.getLogEntriesInRangeAsync(displayStartDate, untilDate, this.docWatcher, { inLogEntriesTimeZone: true });
    const workoutsByDay = mapEntriesByDayAndType(logEntries, LogRecordType.Workout, now);

    // resolving a workout's effective exercises needs the plan and the loader
    // service, so it happens here and the counting rule gets plain objects
    const daysWithExercises = Object.keys(workoutsByDay).sort().map(day => ({
      day,
      exercises: this.workoutLoader.getEffectiveExercises(workoutsByDay[day] as Log_Workout, this.plan, PlanMerge.AsStored),
    }));
    const series = countWorkoutProgressAndSetbacks(daysWithExercises);

    // Get chart colors from CSS variables
    const rootStyles = getComputedStyle(document.documentElement);
    const chartPrimary = rootStyles.getPropertyValue('--chart-primary').trim();
    const chartRed = rootStyles.getPropertyValue('--chart-red').trim();

    this.workoutProgressionDatasets.push({ name: 'Progressions', values: series.progressions, color: chartPrimary });
    // Add red color for regression
    this.workoutProgressionDatasets.push({ name: 'Regressions', values: series.regressions, color: chartRed });

    const totalProgressions = series.progressions.reduce((a, b) => a + b, 0);
    const totalRegressions = Math.abs(series.regressions.reduce((a, b) => a + b, 0));
    if (series.dates.length === 0) {
      this.trainingProgressExplanationMessage = LL.weeklyCheckup.descriptions.noWorkoutsLoggedLastWeek();
    } else {
      this.trainingProgressExplanationMessage = LL.weeklyCheckup.descriptions.workoutProgressionSummary({
        progressions: totalProgressions,
        regressions: totalRegressions,
      });
    }

    this.trendDaysWorkout = series.dates;
  }

  // single-letter weekday initials, ISO order (Mon=0 .. Sun=6)
  protected weekdayInitials: TranslatedString[] = weekdayInitialsMondayFirst();

  async updatePrevWeekdaysLogs() {
    // Clear previous data to avoid stacking when navigating back and forth
    this.prevWeekLogDays = [];

    let now = this.debug.getNowLocal();
    const dayDate = getEntryLocalTimeInDesiredTimeZone(this.logWeeklyCheckup, now);
    const untilDate = dayDate.endOf('day');
    const startDate = untilDate.minus({ days: 6 }).startOf('day');

    let logEntries = await this.userDb.getLogEntriesInRangeAsync(startDate, untilDate, this.docWatcher, { inLogEntriesTimeZone: true });

    const built = buildPreviousWeekdayLogs({
      logEntries,
      days: listDaysInRange(startDate, untilDate),
      weekdayInitials: this.weekdayInitials,
      now,
    });
    this.prevWeekLogDays = built.days;
    // Calculate max target calories for scaling
    this.maxCalories = built.maxCalories;
  }

  async onCompletedCheckupOpened() {

    this.trendingChartsLoading = true;
    this.safeMarkForCheck(this.cd);

    try {
      // Check which charts to show
      const chartsToShow = await this.getBodyCompositionChartsToShow();
      this.showTapeChart = chartsToShow.showTape;
      this.showSkinfoldChart = chartsToShow.showSkinfold;

      // If both are false, default to showing skinfold chart
      if (!this.showTapeChart && !this.showSkinfoldChart) {
        this.showSkinfoldChart = true;
      }

      await this.updateWeighInTrendChartsData();
      await this.updateWorkoutTrendChartData();

    } finally {
      this.trendingChartsLoading = false;
      this.safeMarkForCheck(this.cd);
    }
    void this.loadCheckupOverviewAsync();
  }

  async onCheckupStart() {

    this.trendingChartsLoading = true;
    this.safeMarkForCheck(this.cd);

    try {
      // Check which charts to show
      const chartsToShow = await this.getBodyCompositionChartsToShow();
      this.showTapeChart = chartsToShow.showTape;
      this.showSkinfoldChart = chartsToShow.showSkinfold;

      // If both are false, default to showing skinfold chart
      if (!this.showTapeChart && !this.showSkinfoldChart) {
        this.showSkinfoldChart = true;
      }

      await this.updateWeighInTrendChartsData();
      await this.updateWorkoutTrendChartData();
      await this.updatePrevWeekdaysLogs();

      this.prevWeekRecommendedCalories = Math.round(this.plan.actualDailyAverageIntake);

      // The page guard already ran this once and refused to open the page if it failed, so
      // reaching the failing branch here means it worked at page open and stopped working
      // since -- the inputs are re-read on every call, so a sync landing in between can do it.
      // Stop the user at this step rather than let them finish a check-up on a verdict nobody
      // computed (df-5ba092626ea2); `isNextButtonDisabled` holds the Next button while this is
      // set. It is NOT reported again -- the service already did that under its own name.
      this.hasPlanCheckFailed = false;
      try {
        this.checkForPlanAdjustmentResult = await this.svPlanHandler.checkForPlanAdjustments();
      } catch (error) {
        if (!isPlanCheckFailed(error)) throw error;
        this.hasPlanCheckFailed = true;
        this.checkForPlanAdjustmentResult = undefined;
        this.isBodyCompositionTrendAcceptable = false;
        this.weightInExplanationType = 'warning';
        this.weightInExplanationMessage = LL.weeklyCheckup.errors.progressCheckFailed();
        return;
      }

      // For body composition trends (skinfold/waist), they're acceptable if no adjustment is needed
      // a deliberate "no change needed" counts as acceptable whatever its message says (#6067)
      const progressUndetermined = isProgressCheckUndetermined(this.checkForPlanAdjustmentResult);
      this.isBodyCompositionTrendAcceptable = !this.checkForPlanAdjustmentResult.needsAdjustment && !progressUndetermined;
      if (this.checkForPlanAdjustmentResult.needsAdjustment) {
        this.weightInExplanationType = 'warning';
        switch (this.checkForPlanAdjustmentResult.adjustmentReason) {
          case AdjustmentActionReason.TooMuchWeightLoss:
            this.weightInExplanationMessage = this.getOffTrackMessage('tooMuchWeightLoss');
            break;
          case AdjustmentActionReason.TooMuchFatGain:
            this.weightInExplanationMessage = this.getOffTrackMessage('tooMuchFatGain');
            break;
          case AdjustmentActionReason.NotEnoughFatLoss:
            this.weightInExplanationMessage = this.getOffTrackMessage('notEnoughFatLoss');
            break;
          case AdjustmentActionReason.NotEnoughWeightGain:
            this.weightInExplanationMessage = this.getOffTrackMessage('notEnoughWeightGain');
            break;
          default:
            this.weightInExplanationMessage = this.getOffTrackMessage('generic');
            break;
        }
      } else {
        if (!progressUndetermined) {
          this.weightInExplanationType = 'checkmark';
          this.weightInExplanationMessage = LL.weeklyCheckup.descriptions.weightTrendingWell();
        } else {
          // not enough data, measure more often, or other undetermined messages
          this.weightInExplanationType = 'info';
          this.weightInExplanationMessage = LL.weeklyCheckup.descriptions.weightNotEnoughData();
        }
      }

    } finally {
      this.trendingChartsLoading = false;
      this.safeMarkForCheck(this.cd);
      // also on the early return above (the plan check failed): the cards then show without a badge
      void this.loadCheckupOverviewAsync();
      void this.loadTrainingCountsAsync();
    }
  }

  onProgressReviewNextButtonClick() {

  }

  /**
   * Load the Body Composition overview for this check-up (#5456). Does nothing while the coaching
   * dashboard switch is off, on the coach's review screen (phase 2 gives that its own tab), and
   * for a client whose check-up is waiting for their coach.
   *
   * The cards are fixed on the 14 days ending on the check-up's date, the same days the three
   * trend charts cover. A check-up being filled in shows the wizard's own plan check, never a
   * second one. A completed check-up shows the verdict stored on it; nothing is judged again.
   * Nothing here reads the pending plan or the recommended calories, so a coached client never
   * sees a number their coach has not released.
   */
  protected async loadCheckupOverviewAsync() {
    if (!this.logWeeklyCheckup) return;
    const loads = checkupLoadsOverview({
      coachingDashboardOn: this.coachingDashboardOn,
      isCoachReviewScreen: this.showsCoachReview(),
      isAwaitingCoach: this.isAwaitingCoach(),
    });
    if (!loads) return;
    const loadNumber = ++this.overviewLoadCounter;
    this.overviewLoading = true;
    this.overviewFailed = false;
    this.safeMarkForCheck(this.cd);
    try {
      const now = this.debug.getNowLocal();
      const lastDay = getEntryLocalTimeInDesiredTimeZone(this.logWeeklyCheckup, now);
      const completed = this.isCompleted();
      const request: BodyCompOverviewRequest = completed
        ? completedCheckupOverviewRequest(this.logWeeklyCheckup, now)
        : {
          firstDayIso: lastDay.minus({ days: 13 }).toISODate(),
          lastDayIso: lastDay.toISODate(),
          trendKind: OverviewTrendKind.Straight,
          describesNow: true,
          statusRule: ProgressStatusRule.Checkup,
          planCheck: this.checkForPlanAdjustmentResult,
          checkupInProgress: { checkup: this.logWeeklyCheckup, tickedDayIsos: this.loggedDayDatesToStore },
        };
      const model = await this.overviewData.loadAsync(request, this.docWatcher);
      // if a newer load started, or the page is gone, this answer is stale
      if (this.viewDestroyed || loadNumber !== this.overviewLoadCounter) return;
      this.overviewModel = model;
    } catch (err) {
      if (this.viewDestroyed || loadNumber !== this.overviewLoadCounter) return;
      this.overviewFailed = true;
      // the rest of the check-up keeps working without the cards, so this is reported and not thrown
      this.logging.trackSilentProblem('weeklyCheckupOverviewLoadFailed', { error: describeError(err) });
    } finally {
      if (!this.viewDestroyed && loadNumber === this.overviewLoadCounter) {
        this.overviewLoading = false;
        this.safeMarkForCheck(this.cd);
      }
    }
  }

  /**
   * Load the two counts shown beside the Training Overview button on the Progress Review step
   * (#5456, phase 3). Only for a check-up that is being filled in: a completed one shows the
   * counts stored on it. A failure leaves the button without a count and is reported quietly;
   * the rest of the check-up keeps working.
   */
  private async loadTrainingCountsAsync() {
    // if the switch is off, no count may be kept: the entry guard can read the switch as off
    // before the watcher's callback above has run, and that callback then sees no change
    if (!this.coachingDashboardOn) {
      this.trainingCountsLoadCounter++;
      this.liveTrainingCounts = undefined;
      return;
    }
    if (!this.logWeeklyCheckup || this.showsCoachReview() || this.isCompleted()) return;
    const loadNumber = ++this.trainingCountsLoadCounter;
    // the counts from before this load are no longer known to be true: a pushed page may have
    // changed the program. finishing the check-up before the answer arrives stores no count
    this.liveTrainingCounts = undefined;
    this.safeMarkForCheck(this.cd);
    try {
      const result = await this.trainingOverviewData.loadAsync(
        this.docWatcher, TRAINING_OVERVIEW_WINDOW_DAYS, () => TRAINING_OVERVIEW_SESSIONS_STEP);
      // if a newer load started, the switch went off, or the page is gone, this answer is stale
      if (this.viewDestroyed || loadNumber !== this.trainingCountsLoadCounter || !this.coachingDashboardOn) return;
      this.liveTrainingCounts = { same: result.model.latestSameCount, down: result.model.latestDownCount };
    } catch (err) {
      if (this.viewDestroyed || loadNumber !== this.trainingCountsLoadCounter) return;
      this.liveTrainingCounts = undefined;
      this.logging.trackSilentProblem('weeklyCheckupTrainingCountsLoadFailed', { error: describeError(err) });
    }
    this.safeMarkForCheck(this.cd);
  }

  /**
   * "1 the same, 0 down" beside the Training Overview button, or nothing when there is no count
   * to show. A completed check-up shows the counts stored on it that day; one made before the
   * counts existed, or with the switch off, shows none.
   */
  protected trainingCountText(): string {
    const counts = this.isCompleted() ? readTrainingCounts(this.logWeeklyCheckup) : this.liveTrainingCounts;
    if (!counts || (counts.same === 0 && counts.down === 0)) return '';
    return LL.trainingOverview.checkupCount({ same: counts.same, down: counts.down });
  }

  /** true once the counts beside the Training Overview button are known, also when both are 0 and nothing is drawn */
  protected hasTrainingCounts(): boolean {
    return this.isCompleted() ? !!readTrainingCounts(this.logWeeklyCheckup) : !!this.liveTrainingCounts;
  }

  protected onTrainingOverviewClick(event: Event) {
    // the tap must not reach the card or step behind the button, as with this page's other buttons
    event.stopPropagation();
    void this.navCtrl.pushAsync(ITrainingOverviewPage, {});
  }

  /**
   * The Exercise Settings window, opened from the menu on the coach's Training tab, closed. The
   * return callback is resolved on the page, so the method is here and not on the review component.
   */
  async onCoachTrainingConfigurationResult(context: ExerciseSettingsContext, result: ExerciseConfigurationPageResult) {
    await this.trainingOverviewActions.handleConfigurationResultAsync(context, result);
    if (this.viewDestroyed) return;
    await this.coachReview?.reloadTrainingAsync();
  }

  onWeightTrendSeeAllClick() {
    // Navigate to Progress page showing weight chart for the last year
    const now = this.debug.getNowLocal();
    const checkupDate = getEntryLocalTimeInDesiredTimeZone(this.logWeeklyCheckup, now);
    const untilDate = checkupDate;
    const fromDate = untilDate.minus({ years: 1 });

    const params: ProgressPageParams = {
      startDate: fromDate.toISODate(),
      endDate: untilDate.toISODate(),
      chartType: 'bodyComposition',
      timePeriod: 'year',
    };

    void this.navCtrl.pushAsync(IProgressPage, params);
  }

  onSkinfoldTrendSeeAllClick() {
    // Navigate to Progress page showing caliper/skinfold chart for the last year
    const now = this.debug.getNowLocal();
    const checkupDate = getEntryLocalTimeInDesiredTimeZone(this.logWeeklyCheckup, now);
    const untilDate = checkupDate;
    const fromDate = untilDate.minus({ years: 1 });

    const params: ProgressPageParams = {
      startDate: fromDate.toISODate(),
      endDate: untilDate.toISODate(),
      chartType: 'bodyComposition',
      timePeriod: 'year',
    };

    void this.navCtrl.pushAsync(IProgressPage, params);
  }

  onWaistTrendSeeAllClick() {
    // Navigate to Progress page showing waist/tape chart for the last year
    const now = this.debug.getNowLocal();
    const checkupDate = getEntryLocalTimeInDesiredTimeZone(this.logWeeklyCheckup, now);
    const untilDate = checkupDate;
    const fromDate = untilDate.minus({ years: 1 });

    const params: ProgressPageParams = {
      startDate: fromDate.toISODate(),
      endDate: untilDate.toISODate(),
      chartType: 'bodyComposition',
      timePeriod: 'year',
    };

    void this.navCtrl.pushAsync(IProgressPage, params);
  }

  validity(field: string): TranslatedString {

    switch (field) {
      case 'bf':
        {
          if (!this.immutableState || !this.bfData) {
            break;
          }
          let error = calculateBF(this.bfData, this.gender, this.immutableState.born, this.immutableState.height_cm, bfErrorTexts());
          if (error) {
            return error;
          }
        }
        break;
      case 'calorie-baseline':
        if (!this.selectedCalorieIntakeBaseline) {
          return LL.weeklyCheckup.validation.selectOption();
        }
        if (this.selectedCalorieIntakeBaseline === CalorieIntakeBaseline.Custom && !this.customCalorieIntake) {
          return LL.weeklyCheckup.validation.customCalorieIntake();
        }
        break;
      default:
        return LL.weeklyCheckup.validation.unknownField();
    }
    return null;
  }

  onBodyfatSectionSkipButtonClick() {

  }

  onBodyfatSectionNextButtonClick() {

  }

  bodyfatSkipped() {
    this.bfData.bf_pc = undefined;
    this.bfData.method = undefined;
    this.cd.markForCheck();
  }

  onNutritionLoggingNextButtonClick() {
    const validLoggedDays = this.prevWeekLogDays.filter(day => day.checked);
    const totalCalories = validLoggedDays.reduce((sum, day) => sum + day.consumedCalories, 0);
    this.avgLoggedCalories = validLoggedDays.length > 0 ? Math.round(totalCalories / validLoggedDays.length) : 0;
    // the food step has been answered, so the ticks are now something to store (#5456)
    this.loggedDayDatesToStore = validLoggedDays.map(day => day.dateISO);
    // the overview's intake and adherence cards follow the ticks
    void this.loadCheckupOverviewAsync();
    this.cd.markForCheck();
  }

  getBarHeight(day: WeekdayLogData): number {
    if (day.consumedCalories <= day.targetCalories) {
      return (day.consumedCalories / day.targetCalories) * 100;
    } else {
      const total = day.consumedCalories;
      return (day.targetCalories / total) * 100;
    }
  }

  getExcessBarHeight(day: WeekdayLogData): number {
    if (day.consumedCalories <= day.targetCalories) {
      return 0;
    }
    const excess = day.consumedCalories - day.targetCalories;
    const total = day.consumedCalories;
    return (excess / total) * 100;
  }

  getOuterBarHeight(day: WeekdayLogData): number {
    if (this.maxCalories === 0) {
      return 100;
    }
    return (this.Math.max(day.targetCalories, day.consumedCalories) / this.maxCalories) * 100;
  }

  getFillBarHeight(day: WeekdayLogData): number {
    if (this.maxCalories === 0) {
      return 100;
    }
    const totalConsumed = Math.max(day.consumedCalories, day.targetCalories);
    return (totalConsumed / this.maxCalories) * 100;
  }

  onCheckupResultsNextButtonClick() {

  }

  onLoggedDayCheckedChange(day: WeekdayLogData) {
    day.checked = !day.checked;
    this.cd.markForCheck();
  }

  async customCalorieIntakeBaselineSelected() {
    // First update the selection so the input becomes non-readonly
    this.selectedCalorieIntakeBaseline = CalorieIntakeBaseline.Custom;
    this.cd.markForCheck();

    if (this.customCalorieInput) {
      // Wait for Angular to update the DOM and for the input to become fully interactive
      await waitForAngularUpdate(this.injector);
      if (this.viewDestroyed) return;
      // Call focus which now handles its own timing for keyboard opening
      await this.customCalorieInput.focus();
    }
  }

  // write the body-fat fields the user entered onto the given fitness state. shared by the
  // calorie-baseline step (which also runs the solver) and the finish path (which can run
  // without the calorie-baseline step when progress is undetermined — #3636).
  private applyBfDataToFitnessState(fitnessState: FitnessState): void {
    fitnessState.bfData = deepClone(this.bfData);
    fitnessState.lastBfDataUpdate_utc = this.debug.getNowLocal().toUTC().toISO();
    // store estimated weight at time of BF measurement for LBM baseline calculation
    fitnessState.lastBfDataWeight_kg = this.checkForPlanAdjustmentResult?.estimatedBW_kg ?? fitnessState.weight_kg;
  }

  async onCalorieIntakeBaselineNextButtonClick(): Promise<boolean> {
    // a new solve throws away a target the person set on the last step and came back from (#5456):
    // the reported intake may differ now, so the old number no longer answers the new recommendation
    this.clearOwnTarget();
    this.finalChoice = undefined;
    const loader = this.loadingCtrl.create({
      content: LL.weeklyCheckup.loading.processingData(),
    });
    await loader.present();
    try {
      switch (this.selectedCalorieIntakeBaseline) {
        case CalorieIntakeBaseline.Logged:
          this.calorieIntakeBaseline = this.avgLoggedCalories;
          break;
        case CalorieIntakeBaseline.Target:
          this.calorieIntakeBaseline = this.prevWeekRecommendedCalories;
          break;
        case CalorieIntakeBaseline.Custom:
          if (this.customCalorieIntake) {
            this.calorieIntakeBaseline = this.customCalorieIntake;
          } else {
            this.logging.trackAbnormalError('Custom calorie intake not set, cannot proceed with checkup.');
            return false; // if custom intake is not set, do not proceed
          }
          break;
        default:
          return false; // if the baseline is unknown, do not proceed
      }
      const newPlan = planForCheckupSolve(this.plan, this.calorieIntakeBaseline);
      const declaredState = await this.userDb.loadDeclaredStateAsync(this.docWatcher);
      const newFitnessState = deepClone(await this.userDb.loadFitnessStateAsync(this.docWatcher));
      if (this.showBodyfatEstimationSection && this.bfData && this.bfData.bf_pc) {
        this.applyBfDataToFitnessState(newFitnessState);
      }
      const immutableState = await this.userDb.loadImmutableStateAsync(this.docWatcher);
      const solveOutcome = await this.svPlanHandler.updatePlanAsync(newPlan, declaredState, newFitnessState, immutableState);
      this.clampedToMinimumIntake = solveOutcome.clampedToMinimumIntake;
      this.minimumDailyIntake = readPositiveCalories(solveOutcome.minimumDailyAverageIntake);
      this.recommendedAvgCalories = Math.round(newPlan.actualDailyAverageIntake);

      this.advancedMethodsCalorieCycling = usesCalorieCycling(declaredState);
      this.showsCutAndBulkMeals = declaredState.trainingDays.filter(e => e === true).length > 0 && this.advancedMethodsCalorieCycling;
      if (newPlan && newPlan.actualDailyAverageIntake) {
        const meals = this.readMealCalories(newPlan);
        if (this.showsCutAndBulkMeals) {
          this.recommendedCutMealCalories = meals.cut;
          this.recommendedBulkMealCalories = meals.bulk;
        } else {
          // use fixed meal targets when calorie cycling is disabled
          this.recommendedAvgMealCalories = meals.avg;
        }
      }

      this.newPlan = newPlan;
      this.solverPlan = newPlan;
      this.newFitnessState = newFitnessState;
      return true;
    } catch (err) {
      // if the plan solver fails (e.g. an inconsistent stored plan state), surface a
      // clear error instead of leaving the "Processing data" spinner up forever (#3836)
      this.logging.trackAbnormalException(err, untranslatedString('weekly checkup: updatePlanAsync failed'));
      this.toastCtrl.showToast({
        message: LL.weeklyCheckup.errors.couldNotProcess(),
        duration: 4000,
      });
      return false;
    } finally {
      await loader.dismiss().catch(() => {});
    }
  }

  selectedCalorieIntakeBaselineChanged() {
    this.cd.markForCheck();
  }

  // the analysis found insufficient data to make a recommendation; the rule lives in getProgramUpdateOutcome
  isProgressUndetermined(): boolean {
    return this.programUpdateOutcome() === ProgramUpdateOutcome.Undetermined;
  }

  // which line explains an undetermined outcome (#6067)
  undeterminedReason(): UndeterminedReason {
    return getUndeterminedReason(this.checkForPlanAdjustmentResult?.message);
  }

  // works out which Program Update outcome the APP reached for this checkup. a target the person
  // set themselves never changes this answer: the explanation text, the left button, the coach
  // submission rule and the telemetry all read it (#5456)
  private programUpdateOutcome(): ProgramUpdateOutcome {
    return getProgramUpdateOutcome({
      progressCheck: this.checkForPlanAdjustmentResult,
      clampedToMinimumIntake: this.clampedToMinimumIntake,
      recommendedAvgCalories: this.recommendedAvgCalories,
      calorieIntakeBaseline: this.calorieIntakeBaseline,
      prevWeekRecommendedCalories: this.prevWeekRecommendedCalories,
    });
  }

  /**
   * Whether finishing saves a plan: the app offered a change, or the person applied a target of
   * their own. This is the EFFECTIVE outcome, which only the Accept / Finish button and the save
   * read. Example: BORI is on track at 2,178 and applies 2,120; the app's outcome stays "on
   * track", and this answers yes so Accept saves his plan.
   */
  protected isPlanSaveNeeded(): boolean {
    return getProgramUpdateOutcome({
      progressCheck: this.checkForPlanAdjustmentResult,
      clampedToMinimumIntake: this.clampedToMinimumIntake,
      recommendedAvgCalories: this.recommendedAvgCalories,
      calorieIntakeBaseline: this.calorieIntakeBaseline,
      prevWeekRecommendedCalories: this.prevWeekRecommendedCalories,
      userModifiedCalories: this.userModifiedCalories,
    }) === ProgramUpdateOutcome.Adjust;
  }

  /** the cut, bulk or average meal calories of a plan, whichever this person's plan shows */
  private readMealCalories(plan: FitnessPlan): MealCalories {
    if (this.showsCutAndBulkMeals) {
      return {
        cut: Math.round(plan.cutMealTargets && plan.cutMealTargets[INFOODS.Calories]),
        bulk: Math.round(plan.bulkMealTargets && plan.bulkMealTargets[INFOODS.Calories]),
      };
    }
    return { avg: Math.round(plan.fixedMealTargets && plan.fixedMealTargets[INFOODS.Calories]) };
  }

  /** the number the app itself stands behind: its recommendation when it offers a change, else the target in force */
  protected appRecommendedNumber(): number {
    return appRecommendedCalories(this.programUpdateOutcome(), this.recommendedAvgCalories, this.prevWeekRecommendedCalories);
  }

  /** whether the last step offers Modify; the rule is `canModifyOwnTarget` */
  protected canModifyOwnTarget(): boolean {
    return canModifyOwnTarget({
      coachingDashboardOn: this.coachingDashboardOn,
      hasSolvedPlan: !!this.solverPlan,
      appOutcome: this.programUpdateOutcome(),
      isCoached: this.isCoached(),
      isCoachManaging: this.isCoachManaging(),
    });
  }

  /** the choices the last step lists; empty when there is nothing to choose and the bar offers Finish */
  protected finalChoices(): CheckupFinalChoice[] {
    return finalCheckupChoices({
      coachingDashboardOn: this.coachingDashboardOn,
      isCoachSubmission: this.isCoachSubmissionMode(),
      isAdjustmentNeeded: this.isAdjustmentNeeded(),
      canModifyOwnTarget: this.canModifyOwnTarget(),
    });
  }

  /** the row's title, for example "Accept the New Target" */
  protected finalChoiceLabel(choice: CheckupFinalChoice): TranslatedString {
    switch (choice) {
      case CheckupFinalChoice.AcceptApp: return LL.weeklyCheckup.finalChoice.acceptApp();
      case CheckupFinalChoice.KeepAppTarget: return LL.weeklyCheckup.finalChoice.keepAppTarget();
      case CheckupFinalChoice.SetOwn: return LL.weeklyCheckup.finalChoice.setOwn();
      case CheckupFinalChoice.KeepCurrent: return LL.weeklyCheckup.finalChoice.keepCurrent();
    }
  }

  /** the row's explanation line */
  protected finalChoiceHelp(choice: CheckupFinalChoice): TranslatedString {
    switch (choice) {
      case CheckupFinalChoice.AcceptApp: return LL.weeklyCheckup.finalChoice.acceptAppHelp();
      case CheckupFinalChoice.KeepAppTarget: return LL.weeklyCheckup.finalChoice.keepAppTargetHelp();
      case CheckupFinalChoice.SetOwn: return LL.weeklyCheckup.finalChoice.setOwnHelp();
      case CheckupFinalChoice.KeepCurrent: return LL.weeklyCheckup.finalChoice.keepCurrentHelp();
    }
  }

  /** the daily target a row stands for, shown at its right; Set my own has none until a number is typed */
  protected finalChoiceKcal(choice: CheckupFinalChoice): TranslatedString | undefined {
    switch (choice) {
      case CheckupFinalChoice.AcceptApp: return this.formatCalories(this.recommendedAvgCalories);
      case CheckupFinalChoice.KeepAppTarget: return this.formatCalories(this.appRecommendedNumber());
      case CheckupFinalChoice.KeepCurrent: return this.formatCalories(this.prevWeekRecommendedCalories);
      case CheckupFinalChoice.SetOwn: return undefined;
    }
  }

  /**
   * The person picked a row. Set my own opens the fields on the app's number, so the person edits
   * a number rather than typing one from nothing. Any other row forgets a target typed earlier, so
   * Accept never saves a number the person has walked away from.
   */
  protected onFinalChoiceChange(choice: CheckupFinalChoice) {
    if (this.isFinishing) return;
    this.finalChoice = choice;
    this.clearOwnTarget();
    if (choice === CheckupFinalChoice.SetOwn) {
      this.modifyCalories = this.appRecommendedNumber();
      // put the cursor in the percent field, once the template has drawn it
      this.safeTimeout(() => {
        void (async () => {
          await waitForAngularUpdate(this.injector);
          if (this.viewDestroyed) return;
          await this.ownTargetInput?.focus();
        })();
      }, 0);
    }
    this.cd.markForCheck();
  }

  /** the own-target fields changed: apply the number at once, so the meal figures under them follow it */
  protected onOwnTargetCaloriesChange(calories: number | undefined) {
    this.modifyCalories = calories;
    this.applyTypedOwnTarget();
    this.cd.markForCheck();
  }

  /** true when the typed number is the app's own and no target of the person's is applied: nothing would change */
  protected isModifyUnchanged(): boolean {
    return this.userModifiedCalories === undefined
      && this.modifyCalories !== undefined
      && Math.round(this.modifyCalories) === this.appRecommendedNumber();
  }

  /**
   * Confirm is off until a row is picked, and with Set my own until a number is typed that differs
   * from the app's: the fields are empty, not above zero, or hold the app's own number.
   */
  protected isConfirmDisabled(): boolean {
    if (this.finalChoice === undefined) return true;
    return this.finalChoice === CheckupFinalChoice.SetOwn && this.userModifiedCalories === undefined;
  }

  /** the bar's Confirm, while the switch is on and the last step lists choices */
  onConfirmButtonClick(event: Event) {
    lockClick(event, this.onConfirmAsync());
  }

  /**
   * Confirm runs what the old buttons ran, so the check-up is stored the same way: Keep my
   * current is the old Decline; every other row is the old Accept / Finish, which saves the plan
   * when the app offered a change or the person typed their own target (`isPlanSaveNeeded`).
   */
  private async onConfirmAsync() {
    if (this.isConfirmDisabled()) return;
    if (this.finalChoice === CheckupFinalChoice.KeepCurrent) {
      await this.onCheckupDeclineOrCancelAsync();
    } else {
      await this.onCheckupFinishAsync();
    }
  }

  /**
   * Apply the typed target. Nothing is saved here: Confirm saves it. The plan to save is a copy
   * of the SOLVER's plan scaled to the typed target. An empty field, or the app's own number
   * typed again, takes the person back to the app's plan.
   */
  private applyTypedOwnTarget() {
    this.clearAppliedOwnTarget();
    if (!this.solverPlan || !this.modifyCalories || !(this.modifyCalories > 0)) return;
    const typed = Math.round(this.modifyCalories);
    if (typed === this.appRecommendedNumber()) return;
    const scaledPlan = deepClone(this.solverPlan);
    applyCustomCaloriesToPendingPlan(scaledPlan, typed);
    markTargetChangedIfSolveDidNot(scaledPlan, this.plan, {
      nowUtc: this.debug.getNowLocal().toUTC().toISO(),
      estimatedBW_kg: this.checkForPlanAdjustmentResult?.estimatedBW_kg,
    });
    this.newPlan = scaledPlan;
    this.userModifiedCalories = typed;
    this.modifiedMeals = this.readMealCalories(scaledPlan);
  }

  /** forget a target the person typed, the fields included, and go back to the solver's plan */
  private clearOwnTarget() {
    this.clearAppliedOwnTarget();
    this.modifyCalories = undefined;
  }

  /** go back to the solver's plan; the fields keep what was typed */
  private clearAppliedOwnTarget() {
    this.userModifiedCalories = undefined;
    this.modifiedMeals = {};
    if (this.solverPlan) {
      this.newPlan = this.solverPlan;
    }
  }

  /** where the Decrease / Increase switch starts when the opening number equals the reported intake */
  protected defaultChangeDirection(): CalorieChangeDirection {
    return this.logWeeklyCheckup?.isWeightGainGoal ? CalorieChangeDirection.Increase : CalorieChangeDirection.Decrease;
  }

  /** "2069 kcal (-109 kcal, -5%)": the app's number against the intake the person reported */
  protected formatAppRecommendedWithChange(): TranslatedString {
    const target = this.appRecommendedNumber();
    const diff = target - Math.round(this.calorieIntakeBaseline);
    const delta = LL.coach.checkup.kcalDelta({ sign: diff >= 0 ? '+' : '', diff });
    const change = calorieChangeFromCalories(this.calorieIntakeBaseline, target);
    const value = LL.coach.checkup.kcalValue({ value: target });
    if (!change) {
      return LL.weeklyCheckup.ownTarget.valueWithChange({ value, change: delta });
    }
    const signedPercent = change.direction === CalorieChangeDirection.Decrease ? -change.percent : change.percent;
    return LL.weeklyCheckup.ownTarget.valueWithChange({
      value,
      change: LL.coach.checkup.kcalDeltaWithPercent({ delta, percent: numberText(signedPercent, 1, true) }),
    });
  }

  /** the daily average of the person's own target with its change against the intake they reported */
  protected formatOwnTargetDailyAvgCalories(): TranslatedString {
    const avg = this.userModifiedCalories;
    const diff = Math.round(avg - this.calorieIntakeBaseline);
    return LL.weeklyCheckup.dailyAvgWithDelta({ avg, sign: diff >= 0 ? '+' : '', diff });
  }

  /** the scaled plan's meal figures, as the stats row wants them */
  protected ownTargetMealCalories(): MealCalories {
    return this.modifiedMeals;
  }

  /** the warning under an applied target that is below the lowest target the app would set */
  protected ownTargetBelowMinimumText(): TranslatedString | undefined {
    if (!isBelowMinimumIntake(this.userModifiedCalories, this.minimumDailyIntake)) return undefined;
    return LL.calorieChange.belowMinimum({ kcal: this.userModifiedCalories, minimum: Math.round(this.minimumDailyIntake) });
  }

  /**
   * The line a completed check-up adds when somebody replaced the app's number (#5456): the
   * person themselves, or their coach. Undefined when nobody did, on a check-up made before the
   * fields existed, and while the coaching dashboard switch is off.
   */
  protected completedOwnTargetLine(): TranslatedString | undefined {
    if (!this.coachingDashboardOn || !this.logWeeklyCheckup) return undefined;
    const appNumber = readPositiveCalories(this.logWeeklyCheckup.appRecommendedAvgCalories);
    if (appNumber === undefined) return undefined;
    if (readPositiveCalories(this.logWeeklyCheckup.userModifiedCalories) !== undefined) {
      return LL.weeklyCheckup.ownTarget.completedOwn({ kcal: Math.round(appNumber) });
    }
    if (readPositiveCalories(this.logWeeklyCheckup.coachModifiedCalories) !== undefined) {
      return LL.weeklyCheckup.ownTarget.completedCoach({ kcal: Math.round(appNumber) });
    }
    return undefined;
  }

  isAdjustmentNeeded(): boolean {
    return this.programUpdateOutcome() === ProgramUpdateOutcome.Adjust;
  }

  // progress called for eating less, but the target is already at the minimum daily intake (#5415)
  isAtMinimumIntake(): boolean {
    return this.programUpdateOutcome() === ProgramUpdateOutcome.AtMinimum;
  }

  // the recommended change is 1% of the target or less for another reason, so it is not offered (#3528)
  isChangeTooSmall(): boolean {
    return this.programUpdateOutcome() === ProgramUpdateOutcome.ChangeTooSmall;
  }

  formatDailyAvgCalories(): TranslatedString {
    const avg = Math.round(this.recommendedAvgCalories);
    const diff = Math.round(this.recommendedAvgCalories - this.calorieIntakeBaseline);
    const sign = diff >= 0 ? '+' : '';
    return LL.weeklyCheckup.dailyAvgWithDelta({ avg, sign, diff });
  }

  formatCompletedDailyAvgCalories(): TranslatedString {
    const avg = Math.round(this.logWeeklyCheckup.recommendedAvgCalories);
    const diff = Math.round(this.logWeeklyCheckup.recommendedAvgCalories - this.logWeeklyCheckup.calorieIntakeBaseline);
    const sign = diff >= 0 ? '+' : '';
    return LL.weeklyCheckup.dailyAvgWithDelta({ avg, sign, diff });
  }

  formatCalories(value: number | undefined): TranslatedString | undefined {
    if (value == null) return undefined;
    return LL.coach.checkup.kcalValue({ value: Math.round(value) });
  }

  onCheckupDeclineOrCancelButtonClick(event: Event) {
    lockClick(event, this.onCheckupDeclineOrCancelAsync());
  }

  private async onCheckupDeclineOrCancelAsync() {
    if (this.isFinishing) return;
    this.isFinishing = true;
    if (this.isAdjustmentNeeded()) {
      // User declined adjustments - save checkup data without applying plan changes
      await this.storeCheckupData(WeeklyCheckupCompletionAction.Declined);
      // Don't clear backlog cache since we're not modifying the plan
    }
    // Navigate back without the guard asking. This method serves two buttons:
    // declining the adjustments, which has just been written above, and a plain
    // Cancel, which writes nothing. Neither should be asked a second time — the
    // first because the answer is already stored, the second because Cancel has
    // already said "throw it away".
    this.leaveWithoutAsking.set();
    await this.navCtrl.popAsync();
  }

  getBodyfatEstimationMethodName(method: BFMethod): TranslatedString {
    switch (method) {
      case BFMethod.Caliper:
        return LL.weeklyCheckup.bfMethods.calipers();
      case BFMethod.Tape:
        return LL.weeklyCheckup.bfMethods.tape();
      case BFMethod.Self:
        return LL.weeklyCheckup.bfMethods.visual();
      default:
        return LL.weeklyCheckup.bfMethods.unknown();
    }
  }

  async storeCheckupData(completionAction: WeeklyCheckupCompletionAction) {
    this.logWeeklyCheckup.calorieIntakeBaseline = this.calorieIntakeBaseline;
    // which calorie figures get recorded is `checkupRecordValues`' rule, where a unit test can reach
    // it: the app's own figures as before, the target and meals in force when the app kept the
    // target (#5415, #3528), or the person's own target when they accepted one (#5456)
    const mealCaloriesInForce = (targets: { [key: string]: number } | undefined): number | undefined =>
      targets?.[INFOODS.Calories] !== undefined ? Math.round(targets[INFOODS.Calories]) : undefined;
    const recorded = checkupRecordValues({
      isAccepted: completionAction === WeeklyCheckupCompletionAction.Accepted,
      appOutcome: this.programUpdateOutcome(),
      recommendedAvgCalories: this.recommendedAvgCalories,
      prevWeekRecommendedCalories: this.prevWeekRecommendedCalories,
      usesCalorieCycling: !!this.advancedMethodsCalorieCycling,
      solverMeals: { avg: this.recommendedAvgMealCalories, cut: this.recommendedCutMealCalories, bulk: this.recommendedBulkMealCalories },
      mealsInForce: {
        avg: mealCaloriesInForce(this.plan.fixedMealTargets),
        cut: mealCaloriesInForce(this.plan.cutMealTargets),
        bulk: mealCaloriesInForce(this.plan.bulkMealTargets),
      },
      modification: this.userModifiedCalories !== undefined
        ? { calories: this.userModifiedCalories, meals: this.modifiedMeals }
        : undefined,
    });
    this.logWeeklyCheckup.recommendedAvgCalories = recorded.recommendedAvgCalories;
    // the completed view shows a note for these instead of the discarded solve's numbers
    if (recorded.atMinimumIntake) {
      this.logWeeklyCheckup.atMinimumIntake = true;
    }
    if (recorded.changeTooSmall) {
      this.logWeeklyCheckup.changeTooSmall = true;
    }
    this.logWeeklyCheckup.selectedCalorieIntakeBaseline = this.selectedCalorieIntakeBaseline;
    if (this.bfData.bf_pc !== undefined) {
      this.logWeeklyCheckup.bfData = this.bfData;
    }
    // store meal-level calories; these also reach the coach's notification email
    if (this.advancedMethodsCalorieCycling) {
      this.logWeeklyCheckup.cutMealCalories = recorded.cutMealCalories;
      this.logWeeklyCheckup.bulkMealCalories = recorded.bulkMealCalories;
    } else {
      this.logWeeklyCheckup.avgMealCalories = recorded.avgMealCalories;
    }
    // #5456: the person's own target and the number of the app's it replaced, only when they accepted one
    if (recorded.userModifiedCalories !== undefined) {
      this.logWeeklyCheckup.userModifiedCalories = recorded.userModifiedCalories;
      this.logWeeklyCheckup.appRecommendedAvgCalories = recorded.appRecommendedAvgCalories;
    }
    // #5456: the lowest target the solve would have set, on every completion path and whatever the
    // coaching dashboard switch says, so a check-up submitted before a coach's switch is turned on
    // still carries it. it is not a recommendation and no client screen shows it
    if (this.minimumDailyIntake !== undefined) {
      this.logWeeklyCheckup.minimumDailyIntake = this.minimumDailyIntake;
    }
    // #5456: keep the ticked days and what the progress check said, on every completion path and
    // whatever the coaching dashboard switch says, so the data exists before the switch is turned on.
    // the snapshot holds no calorie recommendation, so storing it while a coach's answer is pending
    // shows the client nothing they may not see.
    if (this.loggedDayDatesToStore !== undefined) {
      this.logWeeklyCheckup.loggedDayDates = this.loggedDayDatesToStore;
    }
    // #5456, phase 3: the two Training Overview counts as the Progress Review step showed them, on
    // every completion path. they are loaded only while the coaching dashboard switch is on, so a
    // check-up completed with the switch off stores neither. they count the person's own logged
    // sets, so storing them while a coach's answer is pending shows the client nothing new.
    if (this.liveTrainingCounts) {
      this.logWeeklyCheckup.trainingSameCount = this.liveTrainingCounts.same;
      this.logWeeklyCheckup.trainingDownCount = this.liveTrainingCounts.down;
    }
    const progressSnapshot = buildCheckupProgressSnapshot(this.checkForPlanAdjustmentResult);
    if (progressSnapshot) {
      this.logWeeklyCheckup.progressSnapshot = progressSnapshot;
    }
    this.logWeeklyCheckup.completionAction = completionAction;
    this.logWeeklyCheckup.state = LogRecordState.Done;
    // persist pending solver output for AwaitingCoach so the coach can resolve later
    if (completionAction === WeeklyCheckupCompletionAction.AwaitingCoach) {
      this.logWeeklyCheckup.pendingNewPlan = this.newPlan ? JSON.stringify(this.newPlan) : undefined;
      this.logWeeklyCheckup.pendingNewFitnessState = this.newFitnessState ? JSON.stringify(this.newFitnessState) : undefined;
    }
    await this.userDb.saveEntryAsync(DatabaseWrapper.getTransactionId(), this.logWeeklyCheckup);

    // checkup done — suppress today's weekly-checkup reminder (fire-and-forget)
    void this.notificationService.suppressForEntryAsync(this.logWeeklyCheckup, this.debug.getNowLocal());

    this.telemetry.trackEvent('weekly_checkup_completed', {
      action: completionAction,
      adjustment_needed: this.isAdjustmentNeeded(),
      at_minimum_intake: this.isAtMinimumIntake(),
      change_too_small: this.isChangeTooSmall(),
      progress_undetermined_reason: this.isProgressUndetermined() ? this.undeterminedReason() : undefined,
      // #5456: the target the person set themselves and accepted, else null. the three flags
      // above still describe what the APP worked out, not what was saved
      user_modified_calories: recorded.userModifiedCalories ?? null,
      has_bf_update: !!(this.showBodyfatEstimationSection && this.bfData && this.bfData.bf_pc),
    });

    // #4625 — a completed checkup is one of the four moments worth asking for a
    // rating after. Placed here rather than in the finish handler because this
    // is the one funnel every completion action passes through, and the action
    // is exactly what decides whether this was a win.
    this.considerReviewPromptAfterCheckup(completionAction);

    // track first check-in for email flow
    this.telemetry.setUserPropertiesOnce({ first_checkin_at: DateTime.utc().toISO() });

    // notify coach if client has one (not for AwaitingCoach — separate notification fires from submitToCoachAsync)
    if (completionAction !== WeeklyCheckupCompletionAction.AwaitingCoach) {
      this.notifyCoachOfCheckup(completionAction);
    }
  }

  private notifyCoachOfCheckup(completionAction: WeeklyCheckupCompletionAction) {
    const coachRel = this.userDb.loadCoachRelAsClient();
    if (!coachRel?.coachUid) return;

    const params: CoachNotifyCheckupParams = {
      type: 'checkupComplete',
      clientDisplayName: this.auth.user?.name || this.auth.user?.uid,
      checkupDateUtc: this.logWeeklyCheckup.time_utc,
      // travels WITH the instant — the pair is what makes a wall-clock time (#4903)
      checkupDateOffsetMin: this.logWeeklyCheckup.timeOffset_min,
      checkupLogId: this.logWeeklyCheckup._id,
      completionAction,
      calorieIntakeBaseline: this.logWeeklyCheckup.calorieIntakeBaseline,
      selectedBaseline: this.logWeeklyCheckup.selectedCalorieIntakeBaseline,
      recommendedAvgCalories: this.logWeeklyCheckup.recommendedAvgCalories,
      isWeightGainGoal: this.logWeeklyCheckup.isWeightGainGoal,
      avgMealCalories: this.logWeeklyCheckup.avgMealCalories,
      cutMealCalories: this.logWeeklyCheckup.cutMealCalories,
      bulkMealCalories: this.logWeeklyCheckup.bulkMealCalories,
      bfPercent: this.logWeeklyCheckup.bfData?.bf_pc,
    };

    void this.coachNotify.queueNotificationAsync(params);
  }

  private notifyCoachOfCheckupAwaiting() {
    const coachRel = this.userDb.loadCoachRelAsClient();
    if (!coachRel?.coachUid) return;

    const params: CoachNotifyCheckupAwaitingParams = {
      type: 'checkupAwaitingReview',
      clientDisplayName: this.auth.user?.name || this.auth.user?.uid,
      checkupDateUtc: this.logWeeklyCheckup.time_utc,
      // travels WITH the instant — the pair is what makes a wall-clock time (#4903)
      checkupDateOffsetMin: this.logWeeklyCheckup.timeOffset_min,
      checkupLogId: this.logWeeklyCheckup._id,
      calorieIntakeBaseline: this.logWeeklyCheckup.calorieIntakeBaseline,
      selectedBaseline: this.logWeeklyCheckup.selectedCalorieIntakeBaseline,
      recommendedAvgCalories: this.logWeeklyCheckup.recommendedAvgCalories,
      isWeightGainGoal: this.logWeeklyCheckup.isWeightGainGoal,
      avgMealCalories: this.logWeeklyCheckup.avgMealCalories,
      cutMealCalories: this.logWeeklyCheckup.cutMealCalories,
      bulkMealCalories: this.logWeeklyCheckup.bulkMealCalories,
      bfPercent: this.logWeeklyCheckup.bfData?.bf_pc,
    };

    void this.coachNotify.queueNotificationAsync(params);
  }

  // client submits a coached checkup for coach review — used for steps 1-5 + submit path
  onSubmitToCoachButtonClick(event: Event) {
    lockClick(event, this.submitToCoachAsync());
  }

  private async submitToCoachAsync() {
    if (this.isFinishing) return;

    // defensive: the Submit-to-Coach button is only shown to coached clients, but if the coach
    // relationship disappeared between render and click, fall back to the normal completion flow
    // rather than stranding the checkup in AwaitingCoach with no coach to resolve it.
    const coachRel = this.userDb.loadCoachRelAsClient();
    if (!coachRel?.coachUid) {
      this.logging.trackSilentProblem('submitToCoach without coach', { checkupId: this.logWeeklyCheckup?._id });
      await this.onCheckupFinishAsync();
      return;
    }

    this.isFinishing = true;

    const hasBfDataUpdate = this.showBodyfatEstimationSection && this.bfData && this.bfData.bf_pc;
    const loader = this.loadingCtrl.create({
      content: LL.weeklyCheckup.loading.finishingCheckup(),
    });

    try {
      await loader.present();

      // save BF data immediately — it's client data, not a plan decision
      if (hasBfDataUpdate) {
        const fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);
        if (this.newFitnessState) {
          deepOverwriteCouchSafe(fitnessState, this.newFitnessState);
        } else {
          // submit-to-coach only shows when an adjustment is needed, so newFitnessState is
          // normally built by the calorie-baseline step. guard anyway so a future change to
          // that gating can't silently drop the body-fat reading (same shape as #3636).
          this.applyBfDataToFitnessState(fitnessState);
        }
        await this.userDb.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
        this.pubSub.publish(BodyCompositionDataChangedEvent, {});
      }

      // store pending solver output in checkup doc so coach can resolve later
      await this.storeCheckupData(WeeklyCheckupCompletionAction.AwaitingCoach);

      // queue coach notification (email + push via the new type)
      this.notifyCoachOfCheckupAwaiting();

      // reassure the user that the submission landed
      this.toastCtrl.showToast({
        message: LL.coach.checkup.submittedToast(),
        duration: 3500,
      });
      // the answers have gone to the coach, so there is nothing left to ask about
      this.leaveWithoutAsking.set();
      await this.navCtrl.popAsync();
    } catch (err) {
      this.isFinishing = false;
      if (this.viewDestroyed) return;
      this.cd.markForCheck();
      this.logging.trackAbnormalException(err, untranslatedString('weekly checkup submit-to-coach failed'));
    } finally {
      await loader.dismiss().catch(() => {});
    }
  }

  onCheckupFinishButtonClick(event: Event) {
    lockClick(event, this.onCheckupFinishAsync());
  }

  private async onCheckupFinishAsync() {
    if (this.isFinishing) return;
    this.isFinishing = true;

    // user entered new body fat data during this checkup
    const hasBfDataUpdate = this.showBodyfatEstimationSection && this.bfData && this.bfData.bf_pc;

    const loader = this.loadingCtrl.create({
      content: LL.weeklyCheckup.loading.finishingCheckup(),
    });

    try {
      // the effective outcome: the app offered a change, or the person applied a target of their own (#5456)
      if (this.isPlanSaveNeeded()) {
        await loader.present();

        // Create backup of the plan and store it in checkup data for future reference.
        const fitnessPlanBackup = await this.userDb.createCurrentPlanBackupLog(DatabaseWrapper.getTransactionId());
        this.logWeeklyCheckup.fitnessPlanBackupId = fitnessPlanBackup._id;

        const fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);
        deepOverwriteCouchSafe(fitnessState, this.newFitnessState);
        await this.userDb.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
        const currentPlan = await this.userDb.loadPlanAsync(this.docWatcher);
        deepOverwriteCouchSafe(currentPlan, this.newPlan);
        await this.userDb.savePlanAsync(DatabaseWrapper.getTransactionId());
        await this.storeCheckupData(WeeklyCheckupCompletionAction.Accepted);
        await this.userDb.clearBacklogCacheAsync();
        this.pubSub.publish(BodyCompositionDataChangedEvent, {});

        await loader.dismiss();
      } else {
        // save fitness state if body fat data was updated even without plan adjustment
        if (hasBfDataUpdate) {
          await loader.present();

          const fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);
          if (this.newFitnessState) {
            deepOverwriteCouchSafe(fitnessState, this.newFitnessState);
          } else {
            // the calorie-baseline step was skipped (progress undetermined), so newFitnessState
            // was never built. write the body-fat fields directly so the new reading still lands
            // in fitness.bfData instead of being silently dropped (#3636).
            this.applyBfDataToFitnessState(fitnessState);
          }
          await this.userDb.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
          this.pubSub.publish(BodyCompositionDataChangedEvent, {});
          await this.storeCheckupData(WeeklyCheckupCompletionAction.Finished);

          await loader.dismiss();
        } else {
          await this.storeCheckupData(WeeklyCheckupCompletionAction.Finished);
        }
      }

      // the check-up is finished and written, so the guard has nothing to ask about
      this.leaveWithoutAsking.set();
      await this.navCtrl.popAsync();
      // #4625 — this page is gone now, so the armed prompt may fire. Doing it
      // here rather than at the decision means the delay is measured from the
      // close, which is what the design requires.
      this.fireArmedReviewPromptIfAny();
    } catch (err) {
      this.isFinishing = false;
      if (this.viewDestroyed) return;
      this.cd.markForCheck();
      await loader.dismiss().catch(() => {});
      this.logging.trackAbnormalException(err, untranslatedString('weekly checkup finish failed'));
    }
  }

  bodyfatPercentChanged() {
    // Only trigger update if we're still on the bodyfat step
    if (this.currentStep > WeeklyCheckupStep.BodyfatEstimation) {
      return;
    }
    this.cd.markForCheck();
  }

  getCompletionActionLabel(): TranslatedString {
    if (!this.logWeeklyCheckup.completionAction) {
      // empty-string sentinel: no completion action recorded yet, so the caller's
      // value-text span renders blank
      return untranslatedString('');
    }
    switch (this.logWeeklyCheckup.completionAction) {
      case WeeklyCheckupCompletionAction.Accepted:
        return LL.weeklyCheckup.completionActions.accepted();
      case WeeklyCheckupCompletionAction.Declined:
        return LL.weeklyCheckup.completionActions.declined();
      case WeeklyCheckupCompletionAction.Finished:
        return LL.weeklyCheckup.completionActions.finished();
      case WeeklyCheckupCompletionAction.AwaitingCoach:
        return LL.coach.checkup.awaitingReview();
      default:
        // unknown/legacy completionAction value: same blank-render fallback as above
        return untranslatedString('');
    }
  }

  private getOffTrackMessage(reason: 'tooMuchWeightLoss' | 'tooMuchFatGain' | 'notEnoughFatLoss' | 'notEnoughWeightGain' | 'generic'): TranslatedString {
    const isCutting = !this.logWeeklyCheckup.isWeightGainGoal;
    let summary: TranslatedString;
    let action: TranslatedString | '';

    switch (reason) {
      case 'tooMuchWeightLoss':
        summary = isCutting
          ? LL.common.offTrack.tooMuchWeightLoss.cutting.summary()
          : LL.common.offTrack.tooMuchWeightLoss.bulking.summary();
        action = isCutting
          ? LL.common.offTrack.tooMuchWeightLoss.cutting.action()
          : LL.common.offTrack.tooMuchWeightLoss.bulking.action();
        break;
      case 'tooMuchFatGain':
        summary = isCutting
          ? LL.common.offTrack.tooMuchFatGain.cutting.summary()
          : LL.common.offTrack.tooMuchFatGain.bulking.summary();
        action = isCutting
          ? LL.common.offTrack.tooMuchFatGain.cutting.action()
          : LL.common.offTrack.tooMuchFatGain.bulking.action();
        break;
      case 'notEnoughFatLoss':
        // only exists for cutting
        summary = LL.common.offTrack.notEnoughFatLoss.cutting.summary();
        action = LL.common.offTrack.notEnoughFatLoss.cutting.action();
        break;
      case 'notEnoughWeightGain':
        // only exists for bulking
        summary = LL.common.offTrack.notEnoughWeightGain.bulking.summary();
        action = LL.common.offTrack.notEnoughWeightGain.bulking.action();
        break;
      case 'generic':
      default:
        summary = LL.common.offTrack.generic.summary();
        action = '';
        break;
    }

    // when action is present, both summary and action are already-localized LL() output; concatenation just composes them
    return action ? untranslatedString(`${summary} ${action}`) : summary;
  }

  /**
   * Which of the two body-composition charts have something to draw.
   *
   * Both answers come from the same weigh-in, so this reads the week once and
   * asks twice. It used to be two methods that each did their own read of the
   * same window, and both call sites ran them together.
   */
  private async getBodyCompositionChartsToShow(): Promise<{ showTape: boolean; showSkinfold: boolean }> {
    const now = this.debug.getNowLocal();
    const dayDate = getEntryLocalTimeInDesiredTimeZone(this.logWeeklyCheckup, now);
    const endDate = dayDate.endOf('day');
    const startDate = endDate.minus({ days: 7 }).startOf('day');

    const logEntries = await this.userDb.getLogEntriesInRangeAsync(startDate, endDate, this.docWatcher, { inLogEntriesTimeZone: true });
    const latestBFLog = pickLatestBodyfatLog(logEntries);

    return {
      showTape: hasWaistMeasurement(latestBFLog),
      showSkinfold: hasSkinfoldMeasurement(latestBFLog),
    };
  }
}
